import React, { useEffect, useMemo, useState } from "react";
import { Button } from "../components/ui/button";
import {
  Search,
  Upload,
  Download,
  Copy,
  MoreHorizontal,
  FileCode,
  ArrowLeft,
  Plus,
  Eye,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  useUpdateContentMutation,
  getContents,
  useContentsQuery,
  useDeleteContentMutation,
  useUploadJupyterFileMutation,
} from "../api/jupyterManagementApi";
import { useJupyterWorkspace } from "../contexts/JupyterWorkspaceContext";
import paths from "../routes";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../App";

type ScriptType = "Project File" | "Template" | "User Created";

interface ScriptItem {
  id: string;
  name: string;
  description?: string;
  author?: string;
  lastModified?: string;
  size?: string;
  version?: string;
  type?: ScriptType;
  tags?: string[];
  // path?: string;
}

function getTypeColor(type?: ScriptType) {
  switch (type) {
    case "Project File":
      return "bg-bits-blue/10 text-bits-blue border-bits-blue";
    case "Template":
      return "bg-bits-gold/10 text-bits-gold border-bits-gold";
    case "User Created":
    default:
      return "bg-muted text-muted-foreground border-border";
  }
}

const generateUniqueFilename = (
  originalName: string,
  existingNames: Set<string>
): string => {
  if (!existingNames.has(originalName.toLowerCase())) {
    return originalName;
  }

  const lastDotIndex = originalName.lastIndexOf(".");
  const baseName =
    lastDotIndex !== -1
      ? originalName.substring(0, lastDotIndex)
      : originalName;
  const extension =
    lastDotIndex !== -1 ? originalName.substring(lastDotIndex) : "";
  const suffixMatch = baseName.match(/^(.*?)\((\d+)\)$/);
  let nameWithoutSuffix = suffixMatch ? suffixMatch[1] : baseName;
  let counter = suffixMatch ? parseInt(suffixMatch[2], 10) : 1;
  let candidate = `${nameWithoutSuffix}(${counter})${extension}`;
  while (existingNames.has(candidate.toLowerCase())) {
    counter++;
    candidate = `${nameWithoutSuffix}(${counter})${extension}`;
  }
  return candidate;
};

const getExt = (name?: string) => {
  if (!name) return "";
  const i = name.lastIndexOf(".");
  return i === -1 ? "" : name.slice(i + 1).toLowerCase();
};
const isPy = (name?: string) => getExt(name) === "py";

export default function PythonScriptsPage() {
  const navigate = useNavigate();
  const { workspace } = useJupyterWorkspace();
  const { user } = useAuth();
  const updateContentMutation = useUpdateContentMutation();
  const deleteContentMutation = useDeleteContentMutation();
  const uploadLTIFileMutation = useUploadJupyterFileMutation();
  const queryClient = useQueryClient();
  const {
    data: contentsData,
    isLoading,
    error,
  } = useContentsQuery(workspace?.folderPath || "", workspace?.workspaceType === "local", !!workspace?.folderPath);
  const [searchTerm, setSearchTerm] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { projectId } = useParams();

  const scripts = useMemo(() => {
    if (
      !contentsData ||
      typeof contentsData !== "object" ||
      !("content" in contentsData) ||
      !contentsData.content
    )
      return [];
    const files = contentsData.content;
    return files
      ?.filter((f: any) => f.type === "file" && isPy(f?.name))
      .map((f: any) => ({
        id: f.path ?? f.name,
        name: f.name,
        description: f.description ?? "",
        author: f.author ?? "",
        lastModified: f.last_modified ?? f.lastModified ?? "",
        size: f.size ?? "",
        version: f.version ?? "",
        type: (f.type as ScriptType) ?? "User Created",
        tags: f.tags ?? [],
        path: f.path,
      }));
  }, [contentsData]);

  useEffect(() => {
    if (error) {
      console.error("Failed to fetch scripts:", error);
      toast.error("Failed to load Python scripts");
    }
  }, [error]);

  const filteredScripts = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const base = scripts?.filter((s) => isPy(s?.name));
    if (!q) return base;
    return base.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        (s.description ?? "").toLowerCase().includes(q) ||
        (s.tags ?? []).some((t) => t.toLowerCase().includes(q))
    );
  }, [scripts, searchTerm]);

  const buildTargetPath = (filename: string) => {
    const basePath = workspace?.folderPath;
    return `${basePath}/${filename}`;
  };

  const MAX_FILE_SIZE = 50 * 1024 * 1024;
  const ALLOWED_EXTS = ["py"];

  const handleUpload = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = ".py";
    input.onchange = async (e: any) => {
      const list = (e.target as HTMLInputElement).files;
      const selected = list ? Array.from(list) : [];
      if (!selected.length || !projectId) return;

      if (selected.length > 20) {
        toast.error("You can upload at most 20 files at a time.");
        return;
      }

      const tooLarge = selected.filter((f) => f.size > MAX_FILE_SIZE);
      if (tooLarge.length) {
        toast.error(
          `Some files exceed the 50MB limit: ${tooLarge
            .map((f) => `${f.name} (${(f.size / (1024 * 1024)).toFixed(1)}MB)`)
            .join(", ")}.`
        );
        return;
      }

      const invalid = selected.filter((f) => {
        const ext = f.name.split(".").pop()?.toLowerCase();
        return !ext || !ALLOWED_EXTS.includes(ext);
      });
      if (invalid.length) {
        toast.error(
          `Unsupported file types: ${invalid.map((f) => f.name).join(", ")}.`
        );
        return;
      }

      const existingNames = new Set(
        (scripts ?? []).map((s) => s.name.toLowerCase())
      );
      const dupExisting = selected.filter((f) =>
        existingNames.has(f.name.toLowerCase())
      );
      if (dupExisting.length) {
        toast.error(
          `Already exists: ${dupExisting
            .map((f) => f.name)
            .join(", ")}. Please rename.`
        );
        return;
      }

      const seen = new Map<string, number>();
      const dupInSelection = selected.filter((f) => {
        const k = f.name.toLowerCase();
        seen.set(k, (seen.get(k) ?? 0) + 1);
        return (seen.get(k) ?? 0) > 1;
      });
      if (dupInSelection.length) {
        const names = [...new Set(dupInSelection.map((f) => f.name))];
        toast.error(`Duplicate names in selection: ${names.join(", ")}.`);
        return;
      }

      const loading = toast.loading(`Uploading ${selected.length} files...`);
      try {
        await uploadLTIFileMutation.mutateAsync({ files: selected, projectId, username: user?.id ? `user_${user.id}` : undefined });
        toast.dismiss(loading);
        toast.success(`Uploaded ${selected.length} files successfully.`);
        await queryClient.invalidateQueries({
          queryKey: ["jupyter", "contents"],
        });
        await queryClient.invalidateQueries({ queryKey: ["files", projectId] });
      } catch (error: any) {
        toast.dismiss(loading);
        if (error?.response?.status === 413) {
          toast.error("File too large. Please use a file smaller than 50MB.");
        } else if (error?.response?.status === 415) {
          toast.error("Unsupported file type. Please try a different format.");
        } else if (error?.response?.data?.data?.message) {
          toast.error(`Upload failed: ${error.response.data.data.message}`);
        } else {
          toast.error(
            `Upload failed: ${
              error?.response?.data?.message ?? "An unknown error occurred."
            }`
          );
        }
      }
    };
    input.click();
  };

  const handleDownload = async (script: ScriptItem) => {
    try {
      const filePath = (script as any).path ?? buildTargetPath(script.name);
      const fileModel = await getContents(filePath, workspace?.workspaceType === "local");
      const contentText =
        typeof fileModel?.content === "string" ? fileModel.content : "";
      if (!contentText) {
        toast.error("No content available to download.");
        return;
      }

      const blob = new Blob([contentText], {
        type: "text/plain;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        script.name.endsWith(".py") ? script.name : `${script.name}.py`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Downloading ${script.name}`);
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("Download failed");
    }
  };

  const handleDuplicate = async (script: ScriptItem) => {
    const loadingToast = toast.loading(`Duplicating ${script.name}...`);
    try {
      const resolvePath = (name: string) => {
        const basePath = workspace?.folderPath || "";
        return `${basePath}/${name}`;
      };
      const sourcePath = (script as any).path ?? resolvePath(script.name);

      const sourceModel = await getContents(sourcePath, workspace?.workspaceType === "local");
      const sourceText =
        typeof sourceModel?.content === "string" ? sourceModel.content : "";
      if (!sourceText) {
        toast.error("No content available to duplicate.");
        return;
      }
      // Generate unique -copy name
      const existingNames = new Set(scripts?.map((s) => s.name.toLowerCase()));
      const parts = script.name.split(".");
      const ext = parts.length > 1 ? `.${parts.pop()}` : ".py";
      const base = parts.join(".");
      const mkName = (idx: number | null) =>
        idx === null ? `${base}-copy${ext}` : `${base}-copy-${idx}${ext}`;

      let candidate = mkName(null);
      let idx = 1;
      while (existingNames.has(candidate.toLowerCase())) {
        candidate = mkName(idx++);
      }

      const targetPath = resolvePath(candidate);

      await updateContentMutation.mutateAsync({
        path: targetPath,
        content: sourceText,
        type: "file",
        format: "text",
      });
      await queryClient.invalidateQueries({
        queryKey: ["jupyter", "contents"],
      });
      await queryClient.invalidateQueries({ queryKey: ["files", projectId] });
      toast.success(`Duplicated ${script.name} → ${candidate}`);
    } catch (err: any) {
      console.error("Duplicate failed:", err);
      toast.error(err?.response?.data?.message || "Duplicate failed");
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  const handleCreateNew = async () => {
    const loadingToast = toast.loading("Creating New py file....");
    try {
      const basePath = workspace?.folderPath || "";
      if (!basePath) {
        toast.error("Create File Failed ! No Active Session");
        return;
      }
      const existingNames = new Set(scripts?.map((s) => s.name.toLowerCase()));
      const base = "Untitled";
      const ext = ".py";
      const mkName = (i: number) =>
        i === 0 ? `${base}${ext}` : `${base}-${i}${ext}`;
      let i = 0;
      let filename = mkName(i);
      while (existingNames.has(filename.toLowerCase())) {
        i += 1;
        filename = mkName(i);
      }
      const filePath = `${basePath}/${filename}`;
      const starter = "# Python script\n";

      const res: any = await updateContentMutation.mutateAsync({
        path: filePath,
        content: starter,
        type: "file",
        format: "text",
      });
      await queryClient.invalidateQueries({
        queryKey: ["jupyter", "contents"],
      });
      await queryClient.invalidateQueries({ queryKey: ["files", projectId] });
      toast.success(`Created ${filename}`);
    } catch (err: any) {
      console.error("Create new script failed:", err);
      toast.error(err?.response?.data?.message || "Failed to create script");
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  const handleDelete = async (script: ScriptItem) => {
    if (deletingId) return;
    const isConfirmed = window.confirm(
      `Are you sure you want to delete "${script.name}"? This action cannot be undone.`
    );
    if (!isConfirmed) return;
    setDeletingId(script.id);
    try {
      const filePath = (script as any).path ?? buildTargetPath(script.name);
      await deleteContentMutation.mutateAsync(filePath);
      await queryClient.invalidateQueries({
        queryKey: ["jupyter", "contents"],
      });
      await queryClient.invalidateQueries({ queryKey: ["files", projectId] });
      toast.success(`Deleted ${script.name}`);
    } catch (err: any) {
      console.error("Delete failed:", err);
      toast.error(
        err?.response?.data?.message ||
          err?.response?.data?.data?.message ||
          "Delete failed"
      );
    } finally {
      setDeletingId(null);
    }
  };

  const handleView = (script: ScriptItem) => {
    navigate(paths.notebook.replace(":projectId", projectId!), {
      state: { file: script },
    });
  };

  return (
    <div className="p-4 mt-4 space-y-2 min-h-screen bg-white">
      <div className="flex items-center">
        <Button
          variant="ghost"
          className="p-4 mt-2"
          onClick={() => {
            navigate(`${paths.notebook.replace(":projectId", projectId!)}`);
          }}
        >
          <ArrowLeft className="!h-6 !w-6 text-gray-700" />
        </Button>
        <h2 className="text-xl font-semibold flex gap-2 mt-2">
          Python Scripts
        </h2>
        <div className="ml-auto flex items-center gap-2">
          <Button onClick={handleUpload} variant="secondary" className="mt-4">
            <Upload className="h-4 w-4" />
            Upload script
          </Button>
          <Button
            onClick={handleCreateNew}
            variant="secondary"
            className="mt-4"
          >
            <Plus className="h-4 w-4" />
            New script
          </Button>
        </div>
      </div>

      <p className="text-muted-foreground p-2">
        Manage Python scripts, upload new files, or create new scripts for
        assignments
      </p>

      <div className="relative">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground " />
        <Input
          placeholder="Search scripts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="grid gap-3">
        {filteredScripts?.map((s) => (
          <div key={s.id} className="border rounded-lg p-3 bg-background">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-medium">{s.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleView(s)}
                >
                  <Eye className="h-4 w-4" />
                  Open
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDownload(s)}
                >
                  Download
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDuplicate(s)}
                >
                  Duplicate
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDelete(s)}
                  disabled={deletingId !== null}
                >
                  {deletingId === s.id ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-white" />
                  ) : (
                    <>Delete</>
                  )}
                </Button>
              </div>
            </div>

            <div className="mt-1 text-sm text-muted-foreground">
              {s.description ?? ""}
            </div>

            <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
              <span>📁 {s.size ?? "—"}kb</span>
            </div>

            <div className="mt-2 flex gap-2">
              {(s.tags ?? [])?.map((tag) => (
                <Badge key={tag} variant="outline">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        ))}

        {isLoading ? (
          <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-bits-blue mb-4" />
            <p className="text-lg font-medium text-gray-900">
              Loading scripts...
            </p>
          </div>
        ) : filteredScripts.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
            {searchTerm.trim() !== "" ? (
              <div className="space-y-2">
                <FileCode className="h-12 w-12 text-muted-foreground mx-auto" />
                <p className="text-lg font-medium text-gray-900">
                  No scripts match your search
                </p>
                <p className="text-sm text-muted-foreground">
                  Try adjusting your search term or create a new script
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <FileCode className="h-16 w-16 text-muted-foreground mx-auto" />
                <div className="space-y-2">
                  <p className="text-lg font-medium text-gray-900">
                    No scripts available
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Create a new script or upload an existing one to get started
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
